"use client";

import { useEffect, useRef, useState } from "react";

const DEFAULT_ROOT_MARGIN = "600px";

type InViewportCallback = (inView: boolean) => void;

/**
 * Enter/leave IntersectionObserver manager.
 *
 * Unlike {@link useIntersectionObserver}'s one-shot manager (which unobserves
 * after the first intersection and so can only fire once), this keeps observing
 * and reports *every* enter/leave transition. That's what lets callers track the
 * set of elements that are *currently* near the viewport — and tear down work for
 * elements that have scrolled away.
 *
 * One manager (one observer) is shared across every element using the same
 * options, avoiding the cost of one observer per card.
 */
class InViewportObserverManager {
  private observer: IntersectionObserver | null = null;
  private callbacks = new WeakMap<Element, InViewportCallback>();
  private options: IntersectionObserverInit;

  constructor(options: IntersectionObserverInit) {
    this.options = options;
  }

  private getObserver(): IntersectionObserver {
    if (!this.observer) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.callbacks.get(entry.target)?.(entry.isIntersecting);
        });
      }, this.options);
    }
    return this.observer;
  }

  observe(element: Element, callback: InViewportCallback): void {
    this.callbacks.set(element, callback);
    this.getObserver().observe(element);
  }

  unobserve(element: Element): void {
    this.observer?.unobserve(element);
    this.callbacks.delete(element);
  }

  disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;
  }
}

// One manager per distinct options, shared across every element that uses those
// options — keyed by the viewport-relative options (root is assumed to be the
// viewport, which is the only case this hook is used for).
const managerRegistry = new Map<string, InViewportObserverManager>();

function getManager(
  options: IntersectionObserverInit,
): InViewportObserverManager {
  const key = JSON.stringify({
    rootMargin: options.rootMargin,
    threshold: options.threshold ?? null,
  });
  let manager = managerRegistry.get(key);
  if (!manager) {
    manager = new InViewportObserverManager(options);
    managerRegistry.set(key, manager);
  }
  return manager;
}

/**
 * Calls `onChange(inView)` whenever the attached element enters or leaves the
 * viewport (expanded by `rootMargin`). Returns a ref callback to attach to the
 * element.
 *
 * The latest `onChange` is always invoked without re-subscribing the observer
 * (held in a ref), so callers can pass an inline callback without churn.
 *
 * @example
 * ```tsx
 * const ref = useInViewport((inView) => setIsVisible(inView));
 * return <div ref={ref}>...</div>;
 * ```
 */
export function useInViewport(
  onChange: InViewportCallback,
  options?: IntersectionObserverInit,
): (node: HTMLElement | null) => void {
  const onChangeRef = useRef(onChange);
  const [element, setElement] = useState<HTMLElement | null>(null);

  // Keep the latest callback without re-subscribing the observer.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const rootMargin = options?.rootMargin ?? DEFAULT_ROOT_MARGIN;
  const threshold = options?.threshold;

  useEffect(() => {
    if (!element) return;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      // No IntersectionObserver (SSR / unsupported): treat as in view so the
      // content isn't permanently deferred.
      onChangeRef.current(true);
      return;
    }

    const manager = getManager({ rootMargin, threshold });
    manager.observe(element, (inView) => onChangeRef.current(inView));

    return () => {
      manager.unobserve(element);
    };
  }, [element, rootMargin, threshold]);

  return setElement;
}

/**
 * Test-only teardown. The manager registry is module-level (so a single observer
 * is shared across every card), which means it leaks between test cases. Call
 * this in `beforeEach` to drop the shared observers and start each test clean.
 */
export function resetInViewportObserversForTests(): void {
  managerRegistry.forEach((manager) => manager.disconnect());
  managerRegistry.clear();
}
